import axios, { AxiosInstance, AxiosRequestConfig } from "axios";
import { t } from "i18next";
import { toast } from "react-toastify";
import AttToken, { refreshAttToken } from "../helpers/attToken";

type RetryableRequestConfig = AxiosRequestConfig & {
  _firebaseTokenRetry?: boolean;
  successMessage?: string;
  errorMessage?: string;
};

const hasExpiredFirebaseIdToken = (error: any) => {
  const responseData = error?.response?.data;
  const details = [
    error?.message,
    responseData?.code,
    responseData?.message,
    responseData?.error,
    typeof responseData === 'string' ? responseData : '',
  ].join(' ');

  return details.includes('auth/id-token-expired');
};

const tokenInData = (data: unknown): string | null => {
  if (data instanceof FormData) {
    const value = data.get('userToken');
    return typeof value === 'string' ? value : null;
  }

  if (typeof data === 'string') {
    try {
      return tokenInData(JSON.parse(data));
    } catch {
      return null;
    }
  }

  if (!data || typeof data !== 'object') return null;
  const value = (data as Record<string, unknown>).userToken;
  return typeof value === 'string' ? value : null;
};

const tokenInHeaders = (headers?: AxiosRequestConfig['headers']): string | null => {
  const entry = Object.entries(headers || {}).find(([key]) => key.toLowerCase() === 'usertoken');
  return typeof entry?.[1] === 'string' ? entry[1] : null;
};

const replaceUserToken = (data: unknown, userToken: string): unknown => {
  if (data instanceof FormData) {
    if (data.has('userToken')) data.set('userToken', userToken);
    return data;
  }

  if (typeof data === 'string') {
    try {
      return JSON.stringify(replaceUserToken(JSON.parse(data), userToken));
    } catch {
      return data;
    }
  }

  if (!data || typeof data !== 'object' || !Object.prototype.hasOwnProperty.call(data, 'userToken')) return data;

  return { ...(data as Record<string, unknown>), userToken };
};

const updateRequestFirebaseToken = (config: RetryableRequestConfig, userToken: string) => {
  const bodyToken = tokenInData(config.data);
  const headerToken = tokenInHeaders(config.headers);
  if (!bodyToken && !headerToken) return config;

  config.data = replaceUserToken(config.data, userToken);
  const headers = { ...(config.headers || {}) };
  for (const key of Object.keys(headers)) {
    if (key.toLowerCase() === 'usertoken') headers[key] = userToken;
    if (key.toLowerCase() === 'authorization' &&
      ((bodyToken && headers[key] === `Bearer ${bodyToken}`) ||
        (headerToken && headers[key] === `Bearer ${headerToken}`))) {
      headers[key] = `Bearer ${userToken}`;
    }
  }
  config.headers = headers;
  return config;
};

const refreshRequestFirebaseToken = async (config: RetryableRequestConfig) => {
  if (!tokenInData(config.data) && !tokenInHeaders(config.headers)) return config;

  const currentAuth = await AttToken();
  return currentAuth ? updateRequestFirebaseToken(config, currentAuth.userToken) : config;
};

const retryWithFreshFirebaseToken = async (error: any, client: AxiosInstance) => {
  const config = error?.config as RetryableRequestConfig | undefined;
  if (!config || config._firebaseTokenRetry || !hasExpiredFirebaseIdToken(error)) return null;
  if (!tokenInData(config.data) && !tokenInHeaders(config.headers)) return null;

  const refreshedAuth = await refreshAttToken();
  if (!refreshedAuth) return null;

  config._firebaseTokenRetry = true;
  return client.request(updateRequestFirebaseToken(config, refreshedAuth.userToken));
};

// Axios interceptors belong to each instance. Legacy services that need their
// own base URL must opt in with this helper, otherwise they would bypass the
// expired-Firebase-token recovery configured below for api and apiv2.
export const applyFirebaseTokenRetry = (client: AxiosInstance) => {
  client.interceptors.request.use(refreshRequestFirebaseToken);
  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      const retry = await retryWithFreshFirebaseToken(error, client);
      if (retry) return retry;

      return Promise.reject(error);
    },
  );

  return client;
};

export const api = axios.create({
  baseURL: process.env.REACT_APP_API_HOST,
});

export const apiv2 = axios.create({
  baseURL: process.env.REACT_APP_AXYMA_MICROSSERVICES_APIS_URL,
});

api.interceptors.request.use(refreshRequestFirebaseToken);
apiv2.interceptors.request.use(refreshRequestFirebaseToken);

api.interceptors.response.use(
  (response) => {
    if (response.config.successMessage) {
      toast.success(response.config.successMessage, { toastId: response.config?.successMessage });
    }
    return response;
  },

  async (error) => {
    const retry = await retryWithFreshFirebaseToken(error, api);
    if (retry) return retry;

    if (error.config?.errorMessage) {
      toast.error(error.config.errorMessage, { toastId: error.config.errorMessage });
    } else if (error.response) {
      const { status } = error.response;

      if (status === 401) {
        toast.error(t("apiErrors.expiredSession"), { toastId: "expiredSession" });
        import("../logout").then(({ logout }) => logout());
      } else if (status === 403) {
        toast.error(t("apiErrors.unauthorized"), { toastId: "unauthorized" });
      } else if (status === 500) {
        toast.error(t("apiErrors.internalError"), { toastId: "internalError" });
      } else if (status === 502) {
        toast.error(t("apiErrors.badGateway"), { toastId: "badGateway" });
      } else if (status === 503) {
        toast.error(t("apiErrors.serviceUnavailable"), { toastId: "serviceUnavailable" });
      } else if (status === 504) {
        toast.error(t("apiErrors.gatewayTimeout"), { toastId: "gatewayTimeout" });
      } else {
        const msg = error.response.data?.message || t("apiErrors.genericError");
        toast.error(String(msg), { toastId: String(msg) });
      }
    } else {
      toast.error(t("apiErrors.networkError"), { toastId: "networkError" });
    }

    return Promise.reject(error);
  }
);

apiv2.interceptors.response.use(
  (response) => {
    if (response.config.successMessage) {
      toast.success(response.config.successMessage, { toastId: response.config?.successMessage });
    }
    return response;
  },

  async (error) => {
    const retry = await retryWithFreshFirebaseToken(error, apiv2);
    if (retry) return retry;

    if (error.config?.errorMessage) {
      toast.error(error.config.errorMessage, { toastId: error.config.errorMessage });
    } else if (error.response) {
      const { status } = error.response;

      if (status === 401) {
        toast.error(t("apiErrors.expiredSession"), { toastId: "expiredSession" });
        import("../logout").then(({ logout }) => logout());
      } else if (status === 403) {
        toast.error(t("apiErrors.unauthorized"), { toastId: "unauthorized" });
      } else if (status === 500) {
        toast.error(t("apiErrors.internalError"), { toastId: "internalError" });
      } else if (status === 502) {
        toast.error(t("apiErrors.badGateway"), { toastId: "badGateway" });
      } else if (status === 503) {
        toast.error(t("apiErrors.serviceUnavailable"), { toastId: "serviceUnavailable" });
      } else if (status === 504) {
        toast.error(t("apiErrors.gatewayTimeout"), { toastId: "gatewayTimeout" });
      } else {
        const msg = error.response.data?.message || t("apiErrors.genericError");
        toast.error(String(msg), { toastId: String(msg) });
      }
    } else {
      toast.error(t("apiErrors.networkError"), { toastId: "networkError" });
    }

    return Promise.reject(error);
  }
);
